import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { deleteDoc, doc } from 'firebase/firestore';
import { Button } from 'src/components/ui/button';
import { Trash2, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { DB } from 'src/contexts/FirebaseContext';

import { PATH_DASHBOARD } from 'src/routes/paths';
import { useSelector } from 'src/redux/store';
import { selectCurrentWorkspaceId } from 'src/redux/slices/userSlice';
import { selectCurrentPlatformIntegrationsMap } from 'src/redux/selectors/integrations';
import { IntegrationState } from 'src/redux/slices/integrationSlice';
import { getIntegration } from 'src/pages/simplyreview/integrations/reviews/Widgets';

interface DeleteWidgetButtonProps {
  widgetId: string;
}

export default function DeleteWidgetButton({ widgetId }: DeleteWidgetButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const workspaceId = useSelector(selectCurrentWorkspaceId) as string;
  const integrationMap = useSelector(selectCurrentPlatformIntegrationsMap);
  const currentWidget = integrationMap[widgetId];
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fallbackTitle = getIntegration(currentWidget?.type);
  const isInactive = currentWidget?.state === IntegrationState.INACTIVE;

  const handleDeleteClick = () => {
    if (!isInactive) {
      toast.error(t('widgets.delete.onlyInactive'));
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!isInactive || !workspaceId || !widgetId) {
      toast.error(t('general.error.default'));
      throw new Error(`Error deleting widget: ${workspaceId} ${widgetId}`);
    }

    try {
      setIsDeleting(true);

      const widgetRef = doc(DB, `workspaceReviews/${workspaceId}/integrations`, widgetId);
      await deleteDoc(widgetRef);

      toast.success(t('widgets.delete.success'));
      navigate(PATH_DASHBOARD.general.integrations);
    } catch (error) {
      console.error('Error deleting widget:', error);
      toast.error(t('general.error.default'));
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Button
              variant={isInactive ? 'destructive' : 'outline'}
              onClick={handleDeleteClick}
              disabled={!isInactive}
              className={`${!isInactive ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('widgets.delete.button')}
            </Button>
          </div>
        </TooltipTrigger>
        {!isInactive && (
          <TooltipContent>
            <p>{t('widgets.delete.onlyInactive')}</p>
          </TooltipContent>
        )}
      </Tooltip>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('widgets.delete.confirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('widgets.delete.confirm.description', {
                widgetTitle: currentWidget?.title || t(fallbackTitle.title),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Trash2 className="w-4 h-4 mr-2" />
              {t('widgets.delete.confirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
